import { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Form, Modal, Skeleton, Switch } from 'antd';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import { UpdateNcmRequest } from '@/core/domain/operational/Ncm/Ncm.types';
import {
  useNcm,
  useDeleteNcm,
  useUpdateNcm,
  useActiveOrInactivateNcm,
} from '@/core/domain/operational/Ncm/Ncm.hooks';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import NcmIncomingForm from '@/components/Fiscal/Ncm/NcmForm/NcmIncomingForm';
import NcmOutgoingForm from '@/components/Fiscal/Ncm/NcmForm/NcmOutgoingForm';
import { SaveButton } from '@/components/_common/Buttons';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

const NcmDetailsPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<UpdateNcmRequest>();
  const { setParamLabel } = useBreadcrumbs();
  const { ncmId } = useRouteMatch<{ ncmId: string }>().params;
  const [active, setActive] = useState(false);

  const updateMutation = useUpdateNcm();
  const deleteMutation = useDeleteNcm();
  const activateOrInactivateMutation = useActiveOrInactivateNcm();

  const { data: ncm, isLoading: isLoadingNcmDetail } = useNcm(Number(ncmId));

  useEffect(() => {
    if (ncm && !isEmpty(ncm)) {
      setParamLabel(':ncmId', ncm.description);

      form.setFieldsValue({
        ...ncm,
        taxesPercentage: ncm.taxesPercentage ? ncm.taxesPercentage : undefined,
      });

      setActive(ncm.active);
    }
  }, [ncm]);

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields();

      const obj = {
        ...values,
        active,
        id: Number(ncmId),
      };

      await updateMutation.mutateAsync(obj);
    } catch {}
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'ncm.remove' }),
      content: `${intl.formatMessage({
        id: 'ncm.remove.alert',
      })} ${ncm?.description}`,
      onOk: async () => {
        if (!ncm) return;

        await deleteMutation.mutateAsync(ncm.id);

        history.push('/app/fiscal/ncm');
      },
      okType: 'danger',
    });
  };

  const handleActiveClick = async (active: boolean) => {
    try {
      activateOrInactivateMutation.mutateAsync(Number(ncmId));

      setActive(active);
    } catch {}
  };

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <SpaceForButton>
          <Switch
            unCheckedChildren={intl.formatMessage({
              id: 'commons.entity.inactive',
            })}
            checkedChildren={intl.formatMessage({
              id: 'commons.entity.active',
            })}
            loading={isLoadingNcmDetail}
            checked={active}
            onChange={handleActiveClick}
            disabled={
              !hasPermissions([
                Authorities.REGISTER_NCM_EDIT,
                Authorities.FISCAL_INVOICE_CHANGE_NCM,
              ])
            }
          />

          <Button
            danger
            onClick={handleRemoveClick}
            loading={deleteMutation.isLoading}
            block
            disabled={!hasPermissions([Authorities.REGISTER_NCM_DELETE])}
          >
            <FormattedMessage id="commons.entity.remove" />
          </Button>

          <SaveButton
            type="primary"
            onClick={handleSaveClick}
            loading={updateMutation.isLoading}
            disabled={
              !hasPermissions([
                Authorities.REGISTER_NCM_EDIT,
                Authorities.FISCAL_INVOICE_CHANGE_NCM,
              ])
            }
            block
          >
            <FormattedMessage id="commons.entity.save" />
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Skeleton loading={isLoadingNcmDetail}>
        <NcmOutgoingForm form={form} />
        <NcmIncomingForm form={form} />

        <Divider />

        <SpaceForButton>
          <ButtonBack path="/app/fiscal/ncm" />
        </SpaceForButton>
      </Skeleton>
    </TabContentWrapper>
  );
};

export default NcmDetailsPage;
